import React, { useState, useEffect, useRef } from 'react';
import {
    Box,
    TextField,
    Checkbox,
    FormControlLabel,
    Autocomplete,
    Button,
    Typography,
    Stack,
} from '@mui/material';
import axios from 'utils/axios';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import { fi } from 'date-fns/locale';
import { t } from 'i18next';

const CodebookEdit = () => {
    const navigate = useNavigate();
    const params = useParams();
    const formRef = useRef();

    const [resolved, setResolved] = useState(null);
    const [values, setValues] = useState({});
    const [valid, setValid] = useState(true);
    const [activeWarning, setActiveWarning] = useState(false);
    const [forward, setForward] = useState(false);

    const [fieldErrors, setFieldErrors] = useState({});




    // Validation rules
    const rules = {
        required: (v) => !!v || v === false,
        requiredNumber: (v) => ((!!v || v === 0) && /^[0-9]*$/.test(v)),
        notRequired: () => true,
    };

    useEffect(() => {
        const fetchData = async () => {
            try {
                const fieldsRes = await axios.get(`/codebook/${params.codebookType}/fields`);
                const valuesRes = await axios.get(`/codebook/${params.codebookType}/${params.id}`);
                const fieldDefs = fieldsRes.data;
                const fieldValues = valuesRes.data;


                const initialValues = {};
                fieldDefs.forEach(field => {
                    initialValues[field.tableField] = fieldValues[field.tableField];
                });


                setValues(initialValues);
                setResolved(fieldDefs);
            } catch (error) {
                console.error('Error loading form data:', error);
            }
        };

        fetchData();
    }, [params]);

    const handleChange = (name, value) => {
        setValues(prev => ({ ...prev, [name]: value }));
    };

    const getFieldType = (type) => {
        switch (type) {
            case 'select': return 'select';
            case 'checkbox': return 'checkbox';
            case '1': return 'number';
            default: return 'text';
        }
    };

    const showField = (mode, rights) => {
        return true; // Always show the field for now
        //return rights?.includes(mode);
    };

    const handleSubmit = async () => {
        let isValid = true;
        const newErrors = {};


        resolved.forEach(field => {
            if (field.required) {
                const value = values[field.tableField];
                const rule = getFieldType(field.type) === 'number' ? rules.requiredNumber : rules.required;
                if (!rule(value)) {
                    isValid = false;
                    newErrors[field.tableField] = t('required_field');
                }
            }
        });

        setFieldErrors(newErrors);
        setValid(isValid);
        if (!isValid) return;

        try {
            await axios.put(`/codebook/${params.codebookType}/${params.id}`, {
                codebook: values,
                codebookType: params.codebookType,
                id: parseInt(params.id),
            });
            toast.success(t('successful_update'));

            setTimeout(() => {
                navigate(`/codebook/${params.codebookType}`);
            }, 1000);

            setForward(true);
            // navigate(`/codebook/${params.codebookType}`);
        } catch (err) {
            toast.error(t('update_failed'));
        }
    };

    if (!resolved) return t('loading');

    return (
        <Box
            sx={{
                p: 4,
                width: '100%',
                maxWidth: 900,              // increased max width for wider form
                mx: 'auto',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'stretch',     // stretch inner content
            }}
        >
            <Typography variant="h4" gutterBottom>
                {t('codebooksEdit')}: {t(params.codebookType)}
            </Typography>
            <br />

            <Box component="form" ref={formRef} sx={{ width: '100%' }}>
                <Stack spacing={3}>
                    {resolved.map((field) =>
                        showField('edit', field.fieldRights) ? (
                            <React.Fragment key={field.tableField}>
                                {getFieldType(field.type) === 'select' && (
                                    <Autocomplete
                                        options={field.options || []}
                                        getOptionLabel={(opt) => opt.name}
                                        value={
                                            field.options?.find(
                                                (opt) => opt.id === values[field.tableField]
                                            ) || null
                                        }
                                        onChange={(e, val) =>
                                            handleChange(field.tableField, val?.id || '')
                                        }
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                required={field.required}
                                                label={field.name}
                                                variant="outlined"
                                                fullWidth
                                            />
                                        )}
                                    />
                                )}

                                {getFieldType(field.type) === 'checkbox' && (
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={!!values[field.tableField]}
                                                onChange={(e) =>
                                                    handleChange(field.tableField, e.target.checked)
                                                }
                                            />
                                        }
                                        label={field.name}
                                    />
                                )}

                                {getFieldType(field.type) === 'number' && (
                                    <TextField
                                        type="tel"
                                        inputProps={{ pattern: '[0-9]*' }}
                                        required={field.required}
                                        label={field.name}
                                        value={values[field.tableField] ?? ''}
                                        onChange={(e) =>
                                            handleChange(field.tableField, e.target.value)
                                        }
                                        error={!!fieldErrors[field.tableField]}
                                        helperText={fieldErrors[field.tableField]}
                                        fullWidth
                                    />
                                )}

                                {getFieldType(field.type) === 'text' && (
                                    <TextField
                                        required={field.required}
                                        label={field.name}
                                        value={values[field.tableField] ?? ''}
                                        onChange={(e) =>
                                            handleChange(field.tableField, e.target.value)
                                        }
                                        error={!!fieldErrors[field.tableField]}
                                        helperText={fieldErrors[field.tableField]}
                                        fullWidth
                                    />
                                )}
                            </React.Fragment>
                        ) : null
                    )}
                </Stack>

                <Stack direction="row" spacing={2} mt={4} justifyContent="flex-end">
                    <Button onClick={() => navigate(`/codebook/${params.codebookType}`)}>
                        {t('cancel')}
                    </Button>
                    <Button variant="contained" onClick={handleSubmit}>
                        {t('save')}
                    </Button>
                </Stack>
            </Box>
        </Box>
    );

};

export default CodebookEdit;
